<template>
  <div v-if="open" class="overlay" role="dialog" aria-modal="true" aria-labelledby="remind-title">
    <div class="card" data-test="remind-dialog" @click.stop>
      <header class="card-head">
        <div class="head-text">
          <h2 id="remind-title">Send reminder</h2>
          <p v-if="preview?.operator_name" class="head-sub" data-test="remind-operator">
            Sending as <strong>{{ preview.operator_name }}</strong> — your name appears in the DM.
          </p>
        </div>
        <button class="close" type="button" aria-label="Close" @click="emitClose">×</button>
      </header>

      <p class="lede">
        DMs each assignee in <strong>{{ sprintName || `sprint ${sprintId}` }}</strong> a short,
        friendly check-in. Tone is drafted by Claude Sonnet for each person and signed off in your name.
      </p>

      <div
        v-if="preview?.test_mode_active"
        class="test-banner"
        role="status"
        data-test="remind-test-banner"
      >
        <span class="badge">🧪 Test mode</span>
        <span class="banner-text">
          Only
          <strong>{{ preview.test_allowlist.join(", ") }}</strong>
          will receive a DM. Everyone else is shown below but skipped.
        </span>
      </div>

      <fieldset class="filter">
        <legend>Which tickets?</legend>
        <label
          v-for="opt in filterOptions"
          :key="opt.id"
          class="filter-opt"
          :class="{ active: filter === opt.id }"
        >
          <input
            v-model="filter"
            type="radio"
            :value="opt.id"
            :data-test="`remind-filter-${opt.id}`"
          />
          <span class="opt-label">{{ opt.label }}</span>
          <span class="opt-hint">{{ opt.hint }}</span>
        </label>
      </fieldset>

      <section class="preview" data-test="remind-preview">
        <header class="preview-head">
          <span class="preview-title">Recipients</span>
          <span v-if="previewLoading" class="preview-status">resolving Slack accounts…</span>
          <span v-else-if="preview" class="preview-status">
            {{ mappedCount }} mapped · {{ preview.unmapped }} unmapped
            <template v-if="preview.skipped_recently > 0">
              · {{ preview.skipped_recently }} on cooldown
            </template>
            <template v-if="preview.skipped_test_mode > 0">
              · {{ preview.skipped_test_mode }} skipped (test mode)
            </template>
          </span>
        </header>

        <div v-if="previewError" class="error" data-test="remind-preview-error">
          {{ previewError }}
          <button class="retry" type="button" @click="loadPreview">Retry</button>
        </div>

        <div v-else-if="previewLoading && !preview" class="skeletons">
          <div class="skel" />
          <div class="skel" />
          <div class="skel" />
        </div>

        <p v-else-if="preview && preview.recipients.length === 0" class="empty">
          No assignees match this filter — nothing to send.
        </p>

        <ul v-else-if="preview" class="recipients">
          <li
            v-for="r in preview.recipients"
            :key="r.email || r.display_name"
            class="row"
            :class="rowClasses(r)"
            :data-test="`remind-recipient-${r.display_name}`"
          >
            <span class="who">
              <span class="name">{{ r.display_name || "(no display name)" }}</span>
              <span class="email">{{ r.email || "(no email)" }}</span>
            </span>
            <span class="count mono">{{ r.issue_count }}</span>
            <span class="status">
              <span class="dot" :class="dotClass(r)" aria-hidden="true" />
              <span class="status-text">{{ statusLabel(r) }}</span>
            </span>
          </li>
        </ul>

        <p
          v-if="preview && cooldownHours > 0"
          class="footnote"
          data-test="remind-cooldown-note"
        >
          Anyone DMed in the last <strong>{{ cooldownHours }}h</strong> is skipped to keep these
          from feeling like spam.
        </p>
      </section>

      <footer class="card-foot">
        <span v-if="sendNotice" class="notice" :class="sendNoticeKind" role="status" aria-live="polite">
          {{ sendNotice }}
        </span>
        <button class="ghost-btn cancel" type="button" :disabled="sending" @click="emitClose">
          Cancel
        </button>
        <button
          v-if="quietHoursBlocked"
          class="ghost-btn danger"
          type="button"
          :disabled="!canSend"
          title="Override the quiet-hours guard and DM assignees now."
          data-test="remind-send-anyway"
          @click="onSend(true)"
        >
          Send anyway
        </button>
        <button
          v-else
          class="ghost-btn primary"
          type="button"
          :disabled="!canSend"
          :title="sendTitle"
          data-test="remind-send"
          @click="onSend()"
        >
          {{ sendLabel }}
        </button>
      </footer>
    </div>
    <div class="scrim" @click="emitClose" />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type {
  ApiError,
  RemindFilter,
  RemindPreview,
  RemindRecipient,
  RemindResult,
} from "@/api/types";
import { previewRemind, sendRemind } from "@/api/sprints";
import { useApi } from "@/composables/useApi";

const props = defineProps<{
  open: boolean;
  projectKey: string;
  sprintId: number;
  sprintName?: string;
}>();

const emit = defineEmits<{
  (e: "update:open", v: boolean): void;
  (e: "sent", result: RemindResult): void;
}>();

const filterOptions: { id: RemindFilter; label: string; hint: string }[] = [
  { id: "stuck", label: "Stuck only", hint: "Tickets idle > 3 days inside the current sprint window. Future and closed sprints produce nothing here." },
  { id: "open", label: "All not-delivered", hint: "Every assigned ticket that hasn't reached a terminal status." },
];

const filter = ref<RemindFilter>("stuck");
const { client } = useApi();

const preview = ref<RemindPreview | null>(null);
const previewLoading = ref(false);
const previewError = ref("");

const sending = ref(false);
const sendNotice = ref("");
const sendNoticeKind = ref<"info" | "error" | "">("");
// Set when the server rejects a send with 409 quiet_hours_active. Surfaces the
// "Send anyway" override button until the operator retries or changes filter.
const quietHoursBlocked = ref(false);

// Recipients eligible to receive a DM right now (mapped + not on cooldown +
// passes the test-mode allowlist when active).
const sendableRecipients = computed<RemindRecipient[]>(() => {
  if (!preview.value) return [];
  return preview.value.recipients.filter(
    (r) =>
      r.slack_user_id &&
      r.status !== "skipped_recently" &&
      r.status !== "skipped_test_mode",
  );
});

const mappedCount = computed(() => {
  if (!preview.value) return 0;
  return preview.value.recipients.filter((r) => r.slack_user_id).length;
});

const cooldownHours = computed(() => preview.value?.per_user_cooldown_hours ?? 0);

const canSend = computed(() => {
  if (sending.value) return false;
  if (!preview.value) return false;
  return sendableRecipients.value.length > 0;
});

const sendLabel = computed(() => {
  if (sending.value) return "Sending…";
  if (!preview.value) return "Send";
  const n = sendableRecipients.value.length;
  if (n === 0) return "Nothing to send";
  return `Send DM to ${n} ${n === 1 ? "person" : "people"}`;
});

const sendTitle = computed(() => {
  if (!preview.value) return "Loading recipients…";
  if (sendableRecipients.value.length === 0) {
    if (preview.value.test_mode_active) {
      return "Nobody in the test allowlist is among this sprint's assignees.";
    }
    if (mappedCount.value === 0) return "No assignees resolved to a Slack account.";
    return "Everyone mapped is on cooldown — nobody new to DM.";
  }
  const bits: string[] = [];
  if (preview.value.unmapped > 0) bits.push(`${preview.value.unmapped} unmapped`);
  if (preview.value.skipped_recently > 0) bits.push(`${preview.value.skipped_recently} on cooldown`);
  if (preview.value.skipped_test_mode > 0) {
    bits.push(`${preview.value.skipped_test_mode} skipped by test mode`);
  }
  if (bits.length) return `Will skip: ${bits.join(", ")}.`;
  return "Send a personalised DM to each assignee with their tickets.";
});

function emitClose(): void {
  if (sending.value) return;
  emit("update:open", false);
}

function setNotice(text: string, kind: "info" | "error"): void {
  sendNotice.value = text;
  sendNoticeKind.value = kind;
}

function rowClasses(r: RemindRecipient): Record<string, boolean> {
  return {
    unmapped: !r.slack_user_id,
    cooldown: r.status === "skipped_recently",
    "test-skipped": r.status === "skipped_test_mode",
  };
}

function dotClass(r: RemindRecipient): string {
  if (r.status === "skipped_test_mode") return "muted";
  if (r.status === "skipped_recently") return "muted";
  return r.slack_user_id ? "ok" : "warn";
}

function statusLabel(r: RemindRecipient): string {
  if (r.status === "skipped_test_mode") return "skipped (test mode)";
  if (!r.slack_user_id) return "no Slack match";
  if (r.status === "skipped_recently" && r.hours_since_last_reminded != null) {
    const h = r.hours_since_last_reminded;
    if (h < 1) return "reminded <1h ago — skipping";
    return `reminded ${Math.round(h)}h ago — skipping`;
  }
  if (r.hours_since_last_reminded != null && r.hours_since_last_reminded < 72) {
    const h = Math.round(r.hours_since_last_reminded);
    return `Slack ✓ · last ${h}h ago`;
  }
  return "Slack ✓";
}

async function loadPreview(): Promise<void> {
  previewLoading.value = true;
  previewError.value = "";
  try {
    preview.value = await previewRemind(client, props.projectKey, props.sprintId, filter.value);
  } catch (err) {
    const e = err as ApiError;
    previewError.value = e.message || "Could not load recipients.";
    preview.value = null;
  } finally {
    previewLoading.value = false;
  }
}

async function onSend(force = false): Promise<void> {
  if (!canSend.value) return;
  sending.value = true;
  quietHoursBlocked.value = false;
  setNotice(
    force ? "Overriding quiet hours — drafting and sending DMs…" : "Drafting messages with Claude and sending DMs…",
    "info",
  );
  try {
    const result = await sendRemind(client, props.projectKey, props.sprintId, filter.value, force);
    const bits = [`${result.sent} sent`];
    if (result.failed) bits.push(`${result.failed} failed`);
    if (result.skipped_recently) bits.push(`${result.skipped_recently} on cooldown`);
    if (result.skipped_test_mode) bits.push(`${result.skipped_test_mode} skipped (test mode)`);
    if (result.unmapped) bits.push(`${result.unmapped} unmapped`);
    setNotice(bits.join(" · "), result.failed > 0 ? "error" : "info");
    emit("sent", result);
    if (result.failed === 0) {
      window.setTimeout(() => emit("update:open", false), 1800);
    }
  } catch (err) {
    const e = err as ApiError;
    const body = (e.body as { error?: string; message?: string; retry_after_seconds?: number } | undefined);
    if (e.status === 409 && body?.error === "quiet_hours_active") {
      quietHoursBlocked.value = true;
      setNotice(body.message || "Reminders pause outside business hours.", "error");
    } else if (e.status === 429) {
      const retry = Number(body?.retry_after_seconds ?? 0);
      setNotice(retry > 0 ? `Cooldown — try again in ${retry}s.` : "Cooldown active.", "error");
    } else {
      setNotice(e.message || "Send failed.", "error");
    }
  } finally {
    sending.value = false;
  }
}

watch(
  () => [props.open, props.projectKey, props.sprintId] as const,
  ([isOpen]) => {
    if (isOpen) {
      sendNotice.value = "";
      sendNoticeKind.value = "";
      quietHoursBlocked.value = false;
      void loadPreview();
    }
  },
  { immediate: true },
);

watch(filter, () => {
  if (props.open) {
    quietHoursBlocked.value = false;
    void loadPreview();
  }
});
</script>

<style scoped>
.overlay {
  position: fixed; inset: 0; z-index: 60;
  display: flex; align-items: center; justify-content: center;
  padding: 16px;
}
.scrim {
  position: absolute; inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(2px);
}
.card {
  position: relative; z-index: 1;
  background: var(--bg-elev, #fff);
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 14px;
  width: min(560px, 100%);
  max-height: min(80vh, 720px);
  overflow: auto;
  padding: 20px 22px 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
  display: flex; flex-direction: column; gap: 14px;
}
.card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.head-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.card-head h2 { font-size: 18px; margin: 0; }
.head-sub { margin: 0; font-size: 12px; color: var(--muted, #6b7280); }
.close {
  background: 0; border: 0; cursor: pointer;
  font-size: 22px; line-height: 1;
  color: var(--muted, #6b7280);
}
.close:hover { color: var(--ink, #111); }

.lede { margin: 0; font-size: 13px; color: var(--muted, #4b5563); }

.test-banner {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 12px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 10px;
  font-size: 12px; color: #78350f;
}
.test-banner .badge {
  font-family: "JetBrains Mono", monospace;
  font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase;
  padding: 2px 8px; border-radius: 999px;
  background: #fbbf24; color: #78350f;
  white-space: nowrap;
}
.test-banner .banner-text { line-height: 1.4; }
.test-banner strong { font-family: "JetBrains Mono", ui-monospace, monospace; }

.filter {
  border: 0; padding: 0; margin: 0;
  display: flex; flex-direction: column; gap: 8px;
}
.filter legend {
  font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--muted, #6b7280);
  margin-bottom: 4px;
}
.filter-opt {
  display: grid; grid-template-columns: 16px 1fr; column-gap: 10px; row-gap: 2px;
  padding: 10px 12px; border-radius: 10px;
  border: 1px solid var(--line, #e5e7eb);
  cursor: pointer;
  align-items: center;
}
.filter-opt:hover { border-color: var(--ink, #111); }
.filter-opt.active { border-color: #2563eb; background: color-mix(in srgb, #2563eb 8%, transparent); }
.filter-opt input { grid-row: span 2; }
.opt-label { font-size: 13px; font-weight: 500; }
.opt-hint { font-size: 11px; color: var(--muted, #6b7280); grid-column: 2; }

.preview { display: flex; flex-direction: column; gap: 8px; }
.preview-head {
  display: flex; align-items: baseline; justify-content: space-between;
}
.preview-title {
  font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--muted, #6b7280);
}
.preview-status {
  font-size: 11px; color: var(--muted, #6b7280);
  font-family: "JetBrains Mono", ui-monospace, monospace;
}

.skeletons { display: flex; flex-direction: column; gap: 6px; }
.skel {
  height: 32px; border-radius: 8px;
  background: linear-gradient(90deg, #f3f4f6, #e5e7eb, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;
}
@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.recipients {
  list-style: none; padding: 0; margin: 0;
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 10px;
  max-height: 260px; overflow: auto;
}
.row {
  display: grid;
  grid-template-columns: 1fr 40px 170px;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid var(--line, #f3f4f6);
  font-size: 13px;
}
.row:last-child { border-bottom: 0; }
.row.unmapped .status-text { color: #b45309; }
.row.cooldown { opacity: 0.6; }
.row.cooldown .status-text { color: var(--muted, #6b7280); }
.row.test-skipped { opacity: 0.5; background: color-mix(in srgb, #fef3c7 35%, transparent); }
.row.test-skipped .status-text { color: #78350f; }

.who { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.name { font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.email {
  font-size: 11px; color: var(--muted, #6b7280);
  font-family: "JetBrains Mono", ui-monospace, monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.count { text-align: right; color: var(--muted, #4b5563); }
.status { display: inline-flex; align-items: center; gap: 6px; font-size: 11px; }
.status-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot.ok { background: #16a34a; }
.dot.warn { background: #f59e0b; }
.dot.muted { background: #9ca3af; }

.empty { font-size: 13px; color: var(--muted, #6b7280); margin: 12px 0; text-align: center; }
.footnote {
  margin: 0; font-size: 11px; color: var(--muted, #6b7280);
  padding-top: 2px;
}

.error {
  font-size: 12px; color: var(--red-ink, #b91c1c);
  background: var(--red-soft, #fee2e2);
  border-radius: 8px;
  padding: 8px 10px;
  display: flex; align-items: center; gap: 8px;
}
.retry {
  background: transparent; border: 1px solid currentColor;
  border-radius: 6px; padding: 2px 8px; cursor: pointer; color: inherit;
  font-size: 11px;
}

.card-foot {
  display: flex; align-items: center; gap: 10px;
  margin-top: 4px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
.notice {
  font-size: 11px; letter-spacing: 0.06em;
  color: var(--muted, #6b7280);
  margin-right: auto;
}
.notice.error { color: var(--red-ink, #b91c1c); }

.ghost-btn {
  font-family: "JetBrains Mono", monospace;
  font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase;
  color: var(--ink-2, #1f2937);
  background: var(--bg-elev, #fff);
  border: 1px solid var(--line, #e5e7eb);
  border-radius: 999px;
  padding: 8px 14px;
  cursor: pointer;
  transition: border-color 120ms ease, transform 120ms ease, background-color 120ms ease;
}
.ghost-btn:hover:not(:disabled) { border-color: var(--ink, #111); transform: translateY(-1px); }
.ghost-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.ghost-btn.primary {
  background: #2563eb; color: #fff; border-color: #1d4ed8;
}
.ghost-btn.primary:hover:not(:disabled) { background: #1d4ed8; }
.ghost-btn.danger {
  background: #b91c1c; color: #fff; border-color: #991b1b;
}
.ghost-btn.danger:hover:not(:disabled) { background: #991b1b; }
</style>
